import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTraduzioni } from '../../i18n';
import { leggiDocumento } from '../../utils/documenti';
import { trattieniFocus } from '../features/GalleriaFoto';
import { messaggioApertura } from './Anteprima';
import VisorePdf from './VisorePdf';
import './anteprima.css';

// «Mai una conferma senza vedere il documento»: ovunque si confermi, verifichi o classifichi un documento, questo
// componente mette accanto l'anteprima grande della prima pagina (zoom, pagine dei PDF) e sotto il testo letto
// dall'OCR, ripiegabile. Per il cliente basta `compatto`: una miniatura cliccabile che apre la stessa anteprima.
//
//   <ConfermaDocumento
//     documento={voce}        // {id, nome_file, content_type, tipo_nome, testo_ocr, parti…}
//     clienteId={clienteId}
//     azioni={<>…bottoni…</>} // "Verifica", "Rifiuta", "Conferma", i menu di chi lo usa: stanno accanto all'anteprima
//     compatto={false}        // true = miniatura cliccabile che apre l'anteprima grande
//     onChiudi={fn}
//   />
//
// In più (tutte facoltative): `testoOcr` se il testo letto non sta nel documento, `miniatura` (il contenuto della
// miniatura: la foto già sul dispositivo, "PDF"…), `leggi` (() => Promise<Blob>, al posto della chiamata al server),
// `etichetta` (il nome del bottone della miniatura), `titolo` (l'intestazione del pannello), `onApri` (la finestra
// grande si è aperta: chi lo usa può togliere dalla riga i comandi che ora stanno accanto all'anteprima).
// Nota: nel riepilogo del caricamento multiplo `riga.testo` è la descrizione ("Busta paga · agosto"), non l'OCR:
// il testo letto si prende solo da `testoOcr`, `testo_ocr`, `ocr` o `testo_letto`.

export const ZOOM = [100, 150, 200];

// Quante pagine ha un PDF, leggendone il contenuto: "/Type /Pages … /Count 3"; se l'albero delle pagine è compresso
// si contano gli oggetti "/Type /Page". null = non si sa (le frecce restano, senza il totale).
//
// Non lo usa più il visore — il numero vero lo dà pdf.js, che il file lo apre davvero (src/utils/pdf.js) — ma resta
// perché è l'unico modo di contare le pagine **senza** caricare pdf.js: serve al riepilogo del caricamento multiplo,
// dove le pagine si contano su file appena scelti e nessuno li sta guardando.
export const contaPagine = (contenuto) => {
  const testo = String(contenuto || '');
  const conti = [...testo.matchAll(/\/Count\s+(\d+)/g)].map((m) => Number(m[1]));
  if (conti.length) return Math.max(...conti) || null;
  const pagine = (testo.match(/\/Type\s*\/Page[^s]/g) || []).length;
  return pagine || null;
};

const testoLetto = (documento, testoOcr) => {
  const testo = testoOcr ?? documento?.testo_ocr ?? documento?.ocr ?? documento?.testo_letto;
  return typeof testo === 'string' && testo.trim() ? testo.trim() : null;
};

// L'anteprima vera e propria: prima pagina grande, zoom, pagine, testo letto sotto
const Visore = ({ documento, clienteId, leggi, testoOcr }) => {
  const { t, l } = useTraduzioni();
  const [stato, setStato] = useState({ url: null, errore: null });
  const [zoom, setZoom] = useState(100);
  const [pagina, setPagina] = useState(1);
  const [pagine, setPagine] = useState(null);
  const [apertoTesto, setApertoTesto] = useState(false);
  // il documento e come leggerlo stanno in una ref: la riga che ci arriva è un oggetto nuovo a ogni render della
  // pagina, e il file si sarebbe riletto ogni volta (una chiamata al server a ogni battito del riepilogo)
  const voce = useRef({ documento, leggi });
  voce.current = { documento, leggi };

  useEffect(() => {
    let url = null;
    let vivo = true;
    const { documento: doc, leggi: chiedi } = voce.current;
    setStato({ url: null, blob: null, errore: null });
    setPagina(1);
    // Quante pagine: di partenza quello che dice il server (`parti`). Il numero vero lo dà il visore quando ha
    // aperto il file (`onPagine`), perché è l'unico che lo sa davvero.
    setPagine(doc.parti && doc.parti > 0 ? doc.parti : null);
    (chiedi ? chiedi() : leggiDocumento(doc, 'apri'))
      .then((blob) => {
        if (!vivo) return;
        url = window.URL.createObjectURL(blob);
        setStato({ url, blob, errore: null });
      })
      .catch((e) => {
        if (!vivo) return;
        const messaggio = messaggioApertura(e?.response?.status, l);
        setStato({ url: null, blob: null, errore: messaggio ? `${messaggio}.` : t('documenti.anteprima.non_aperto') });
      });
    return () => { vivo = false; if (url) window.URL.revokeObjectURL(url); };
  }, [documento.id, documento.content_type, clienteId]); // eslint-disable-line

  const tipo = documento.content_type || '';
  const immagine = tipo.startsWith('image/');
  const pdf = tipo === 'application/pdf';
  const nome = documento.tipo_nome || documento.nome_file || t('documenti.anteprima.documento');
  const ocr = testoLetto(documento, testoOcr);
  const ultima = pagine || null;
  return (
    <div className="cd-visore" data-testid="conferma-visore">
      <div className="cd-barra">
        <span className="cd-zoom" role="group" aria-label={t('documenti.anteprima.ingrandimento')}>
          {ZOOM.map((z) => (
            <button key={z} type="button" className={z === zoom ? 'su' : ''} aria-pressed={z === zoom}
              aria-label={t('documenti.anteprima.ingrandisci', { z })} onClick={() => setZoom(z)}>{z}%</button>
          ))}
        </span>
        {pdf && (!ultima || ultima > 1) && (
          <span className="cd-pagine">
            <button type="button" aria-label={t('documenti.anteprima.pagina_prec')} disabled={pagina <= 1} onClick={() => setPagina((p) => Math.max(1, p - 1))}>‹</button>
            <small data-testid="conferma-pagina">{ultima ? t('documenti.anteprima.pagina_di', { n: pagina, tot: ultima }) : t('documenti.anteprima.pagina', { n: pagina })}</small>
            <button type="button" aria-label={t('documenti.anteprima.pagina_succ')} disabled={Boolean(ultima) && pagina >= ultima}
              onClick={() => setPagina((p) => (ultima ? Math.min(ultima, p + 1) : p + 1))}>›</button>
          </span>
        )}
      </div>
      <div className="cd-foglio">
        {stato.errore && <p role="alert">{stato.errore}</p>}
        {!stato.errore && !stato.url && <p>{t('documenti.anteprima.apro')}</p>}
        {stato.url && immagine && (
          <img src={stato.url} alt={t('documenti.anteprima.anteprima_di', { nome })} data-testid="conferma-immagine" style={{ width: `${zoom}%` }} />
        )}
        {/* Il PDF lo disegniamo noi (pdf.js), non il visore del browser: dentro un iframe su iPhone restava
            bianco, e cambiare pagina ricaricava il documento intero (la `key` che c'era qui). Vedi utils/pdf.js */}
        {stato.blob && pdf && (
          <div data-testid="conferma-pdf">
            <VisorePdf blob={stato.blob} url={stato.url} pagina={pagina} zoom={zoom}
              onPagine={(n) => setPagine(n && n > 0 ? n : null)}
              titolo={t('documenti.anteprima.anteprima_di', { nome })} />
          </div>
        )}
        {stato.url && !immagine && !pdf && <p>{t('documenti.anteprima.non_si_vede')}</p>}
      </div>
      {ocr && (
        <div className="cd-testo">
          <button type="button" aria-expanded={apertoTesto} onClick={() => setApertoTesto((x) => !x)}>
            {apertoTesto ? '▾' : '▸'} {t('documenti.anteprima.testo_letto')}
          </button>
          {apertoTesto && <pre data-testid="conferma-testo-letto">{ocr}</pre>}
        </div>
      )}
    </div>
  );
};

const ConfermaDocumento = ({
  documento, clienteId = null, azioni = null, compatto = false, onChiudi = null,
  testoOcr = null, miniatura = null, leggi = null, etichetta = null, titolo = null, onApri = null,
}) => {
  const { t } = useTraduzioni();
  const [aperto, setAperto] = useState(false);
  const finestra = useRef(null);
  const apri = useRef(null);
  const chiudiOra = useRef(onChiudi);
  chiudiOra.current = onChiudi;

  // La finestra grande è modale come l'anteprima dei documenti: Esc chiude, Tab resta dentro, la pagina sotto non scorre
  useEffect(() => {
    if (!compatto || !aperto) return undefined;
    const tasto = (e) => {
      if (e.key === 'Escape') { setAperto(false); chiudiOra.current?.(); } else trattieniFocus(e, finestra.current);
    };
    document.addEventListener('keydown', tasto);
    finestra.current?.querySelector('.cd-chiudi')?.focus(); // il fuoco entra nella finestra, come nell'anteprima
    const prima = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', tasto); document.body.style.overflow = prima; };
  }, [compatto, aperto]);

  useEffect(() => { if (compatto && !aperto) apri.current?.focus?.(); }, [compatto, aperto]);

  const nome = titolo || documento.tipo_nome || documento.nome_file || t('documenti.anteprima.documento');
  const chiudi = () => { setAperto(false); if (onChiudi) onChiudi(); };
  const pannello = (fisso) => (
    <div className={`conferma-doc${fisso ? ' fisso' : ''}${azioni ? '' : ' solo-visore'}`} data-testid="conferma-documento"
      ref={fisso ? finestra : null} role={fisso ? 'dialog' : undefined} aria-modal={fisso ? 'true' : undefined}
      aria-label={fisso ? t('documenti.anteprima.titolo', { nome }) : undefined}>
      <div className="cd-lato">
        <div className="cd-testa">
          <b>{nome}{documento.nome_file && documento.nome_file !== nome && <small>{documento.nome_file}</small>}</b>
          {(fisso || onChiudi) && (
            <button type="button" className="cd-chiudi" onClick={chiudi} aria-label={t('documenti.anteprima.chiudi_aria')}>{t('comune.chiudi')}</button>
          )}
        </div>
        {azioni && <div className="cd-azioni">{azioni}</div>}
      </div>
      <Visore documento={documento} clienteId={clienteId} leggi={leggi} testoOcr={testoOcr} />
    </div>
  );

  if (!compatto) return pannello(false);
  return (
    <>
      <button type="button" className="cd-apri" ref={apri} data-testid="conferma-apri"
        aria-label={etichetta || t('documenti.lotto.apri_anteprima', { nome })} onClick={() => { setAperto(true); if (onApri) onApri(); }}>
        {miniatura || <span className="cd-mini" aria-hidden="true">{(documento.content_type || '').includes('pdf') ? 'PDF' : 'FILE'}</span>}
      </button>
      {/* la finestra grande sta in fondo alla pagina (portale): dentro la scheda del cliente la barra in alto dello
          staff le finiva davanti e i bottoni dell'anteprima non si potevano premere */}
      {aperto && createPortal(pannello(true), document.body)}
    </>
  );
};

export default ConfermaDocumento;
